import { BaseModel, Tag } from "./common.models";
import { ProgramTier } from "./careplan.models";

export enum UserGender {
    Male = 'M',
    Female = 'F',
}

export enum UserLanguage {
    English = 'en-us',
    Hindi = 'hn',
}

export enum FoodHabit {
    Vegetarian = 'Veg',
    Non_Vegetarian = 'Nonveg',
    Eggetarian = 'Eggetarian',
}

export enum MaritalStatus {
    Married = 'Married',
    Single = 'Single',
}

export enum HousingType {
    Family = 'Family',
    Alone = 'Alone',
    Hostel = 'Hostel',
}

export enum DoctorCategory {
    Premium = 'Premium',
    A = 'A',
    B = 'B',
    C = 'C',
    NEW = 'NEW',
}

export enum DoctorSpeciality {
    chat_agent = '0f7e3b8c-4284-4b44-be90-f9e8663eafbe',
    fitness_instructor = '94f41442-a112-47d4-ab1e-6d5b2f78bc2c',
    dietician = 'd828790d-9770-49cf-aba9-4f054bc03d1c',
    health_coach = '48bc4e0b-a70d-491b-bb56-3e8d68d6e2ee',
    gynaecologiest = '66b70ec2-16c3-4163-bca9-1ebdf77c5177',
    dermatologist = '6a48f63d-2e95-48c2-bdb4-b93632fa4031',
    general_surgery = 'a54ddd23-a5d3-4213-83be-8308b062eac8',
    general_medicine = 'ca5ffd0f-3a68-47b9-8409-7e28b478f927',
    business_development_team = 'af215167-5ba4-42dc-8e48-6d4907150c2d'
}

export interface DietRecallMeal {
    index?: number;
    meal_name: string;
    meal_timing: string;
    meal_options: string;
}

export enum SessionNotOptedReason {
    opt_in = 'Opt In For Session',
    pranayam_only = 'pranayam_only',
    prenatal_yoga_allowed = 'prenatal_yoga_allowed',
    complete_bed_rest = 'complete_bed_rest',
    patient_did_not_pay = 'patient_did_not_pay',
    patient_not_interested = 'Patient Not Interested',
    not_permitted_by_doctor = 'Not Permitted By Doctor',
    waiting_for_doctor = "Waiting for Doctor's Confirmation",
    conception_yoga = 'conception_yoga',
    liss = 'liss',
    hiit = 'hiit'
}

export interface DayFoodHabit {
    day_of_week: number;
    food_habits: FoodHabit
}

export enum PhysicalActivityProbeLevel {
    'Live Attendance' = 'Live Attendance',
    'Recorded Session Attendance' = 'Recorded Session Attendance',
    'Regular Walking' = 'Regular Walking',
    'No Activity Tracking' = 'No Activity Tracking',
}

export interface PatientProfile {
    id: string;
    name: string;
    dob: string;
    age: number;
    gender: UserGender;
    food_habits: FoodHabit;
    day_based_food_habits: DayFoodHabit[];
    language: UserLanguage;
    suitable_follow_up_time: string;
    preferred_timings: string;
    pregnancy_count: number;
    pregnancy_previous_weight_kg: number;
    delivery_date: string;
    postpartum_delivery_type: string;
    marital_status: MaritalStatus;
    housing_type: HousingType;
    office_end_time: string;
    office_start_time: string;
    phone: string;
    alternate_phone: string;
    primary_cuisine: string;
    secondary_cuisine: string[];
    food_likes: string;
    food_dislikes: string;
    height_cm: number;
    weight_in_kg: number;
    has_miscarriage_history: boolean;
    daily_diet_recall: DietRecallMeal[];
    special_care: boolean;
    session_not_opted_reason: SessionNotOptedReason;
    profession: string;
    working_shift: string;
    work_from_home: boolean;
    override_session_link_access_rule: boolean;
    created: string;
    modified: string;
    who_cooks_food: string;
    energy_levels: string;
    appetite: string;
    comfortable_changing_flour: string;
    is_first_pregnancy: string;
    conditions_in_last_pregnancy: string[];
    timezone: string;
    patient_not_on_whatsapp: boolean;
    chat_team_notes: string,
    instructions_to_chat_team: string,
    fitness_instructor_notes: string,
    first_name: string,
    probe_for_meal_picture: boolean,
    probe_for_physical_activity: PhysicalActivityProbeLevel,
    suggest_meal_swaps: boolean,
}

export interface UserRole {
    routes: Set<NavBarRoute>;
    default_route: NavBarRoute | AuthRoute;
    module_permissions: Partial<{
        [key in UserPermissionModules]: {
            features: Set<string>,
            non_hierarchical_features?: Set<string>
        }
    }>;
    inherits_from?: UserRole[];
}

export enum UserPermissionModules {
    /* Users */
    users = 'users',
    doctors = 'doctors',
    /* Careplan */
    prescription = 'prescription',
    enrolment = 'enrolment',
    consultation = 'consultation',
    diet = 'diet',
    lead = 'lead',
    session = 'session',
    /* Messaging */
    channel = 'channel',
    /* Common */
    tags = 'tags',
}

export enum UsersFeatures {
    change_phone_number = 'change_phone_number',
}

export enum DoctorsFeatures {
    view_all = 'view_all',
    view_team = 'view_team',
    create_meet_task = 'create_meet_task',
    change_program_prices = 'change_program_prices',
    change_manager_notes = 'change_manager_notes',
    change_head_office_notes = 'change_head_office_notes',
    review_meetings = 'review_meetings',
    manage_msl = 'manage_msl'
}

export enum PrescriptionFeatures {
    skip_validation = 'skip_validation',
}

export enum EnrolmentFeatures {
    view_all = 'view_all',
    change_owner = 'change_owner',
    change_duration = 'change_duration',
    send_community_link = 'send_community_link',
    view_conversation_starters = 'view_conversation_starters',
    discount_prospect = 'discount_prospect',
    change_phone_number = 'change_phone_number',
    change_instructions_to_chat_team = 'change_instructions_to_chat_team',
    change_chat_team_notes = 'change_chat_team_notes',
    change_fitness_instructor_notes = 'change_fitness_instructor_notes',
    opt_in_to_restricted_batch = 'opt_in_to_restricted_batch',
    view_dietitian_consultation_task = 'view_dietitian_consultation_task',
    view_dietitian_check_in_task = 'view_dietitian_check_in_task',
    view_special_consultation_task = 'view_special_consultation_task',
    view_charge_collection_task = 'view_charge_collection_task',
    view_fitness_instructor_consultation_task = 'view_fitness_instructor_consultation_task',
    view_trial_onboarding_task = 'view_trial_onboarding_task',
    view_psych_emergency_task = 'view_psych_emergency_task',
    create_complementary_special_consultation_task = 'create_complementary_special_consultation_task',
    view_memory = 'view_memory',
    create_dietitian_check_in_tasks = 'create_dietitian_check_in_tasks',
}

export enum LeadFeatures {
    view_restricted_fields = 'view_restricted_fields',
    view_all = 'view_all',
    change_owner = 'change_owner',
    discounted_bill = 'discounted_bill',
    change_phone_number = 'change_phone_number',
    add_qa_comments = 'add_qa_comments',
}

export enum ConsultationFeatures {
    create_custom_event = 'create_custom_event',
    change_owner = 'change_owner',
    change_type = 'change_type',
}

export enum ChannelFeatures {
    view_gpt_replies = 'view_gpt_replies',
    autofill_gpt_replies = 'autofill_gpt_replies',
    send_quick_replies = 'send_quick_replies',
    override_query_validation = 'override_query_validation',
    view_all_queries = 'view_all_queries',
}

export enum TagsFeatures {
    create = 'create',
}

export enum DietFeatures {
    view_all = 'view_all',
    edit_all = 'edit_all',
    approve = 'approve',
}

export enum SessionFeatures {
    view_all = 'view_all',
    edit_all = 'edit_all',
}


export enum NavBarRoute {
    care_dashboard = '/care/dashboard',
    care_tasks = '/care/tasks',
    care_chat = '/care/chat',
    care_patients = '/care/patients',
    care_renewals = '/care/renewals',
    care_patient_assignment = '/care/patient_assignment',
    care_query_assignment = '/care/query_assignment',
    care_diet_chart_template_management = '/care/diet_chart_template_management',
    care_refer_patients = '/care/refer_patients',
    care_whatsapp_chat = '/care/whatsapp_chat',
    care_psych_emergency_tasks = '/care/psych_emergency_tasks',
    care_onboarding_patients = '/care/onboarding_patients',
    care_new_patients = '/care/new_patients',
    care_intensive_care = '/care/intensive_care',
    care_renewal_countdown = '/care/renewal_countdown',
    care_dietitian_consultation_calendar = '/care/dietitian_consultation_calendar',
    bd_dashboard = '/bd/dashboard',
    bd_doctors = '/bd/doctors',
    bd_suggested_prospects = '/bd/suggested_prospects',
    bd_meetings = '/bd/meetings',
    bd_chat = '/bd/chat',
    bd_whatsapp_chat = '/bd/whatsapp_chat',
    bd_prescribe_patient = '/bd/prescribe_patient',
    bd_feedbacks = '/bd/feedbacks',
    bd_send_notification = '/bd/send_notification',
    sessions_calendar = '/sessions/calendar',
    sessions_standalone = '/sessions/standalone',
    sessions_course = '/sessions/course',
    sessions_recurring = '/sessions/recurring',
    sessions_batches = '/sessions/batches',
    sessions_patients = '/sessions/patients',
    sessions_patient_transfer = '/sessions/patient_transfer',
    sessions_tasks = '/sessions/tasks',
    sessions_special_consultation_tasks = '/sessions/special_consultation_tasks',
    sessions_special_consultation_calendar = '/sessions/special_consultation_calendar',
    sessions_webinars = '/sessions/webinars',
    sessions_leaves = '/sessions/leaves',
    sales_tasks = '/sales/tasks',
    sales_patients = '/sales/patients',
    sales_chat = '/sales/chat',
    sales_whatsapp_chat = '/sales/whatsapp_chat',
    sales_patient_assignment = '/sales/patient_assignment/list',
    sales_patient_assignment_rules = '/sales/patient_assignment/rules',
    tools_whatsapp_login = '/tools/whatsapp_login',
    tools_edit_message_template = '/tools/edit_message_template',
    tools_send_message_template = '/tools/send_message_template',
    tools_upload_assets = '/tools/upload_assets',
    tools_knowledge_base = '/tools/knowledge_base',
    tools_query_review = '/tools/query_review',
}

export enum AuthRoute {
    insufficient_access = 'insufficient-access',
    sign_out = 'sign-out',
}

export enum NavBarCategory {
    care = 'care',
    sales = 'sales',
    doctors = 'doctors',
    sessions = 'sessions',
    tools = 'tools',
}

export interface NavBarRouteMetadata {
    name: string;
    route: NavBarRoute;
    icon: string;
    /* used to group route listing in navbar */
    category: NavBarCategory;
    /* used to sort the route listing in navbar */
    order: number;
    /* implement this if to support route guards */
    regexp?: RegExp;
}

export interface NavBarCategoryMetadata {
    name: string;
    category: NavBarCategory;
    order: number;
}

export interface DoctorProfileLite extends BaseModel {
    name: string,
    user_id: string,
}

export enum CLBDArea {
    Delhi = 'Delhi',
    Remote = 'Remote',
    Gurugram = 'Gurugram',
    Mumbai = 'Mumbai',
    Jaipur = 'Jaipur',
    Lucknow = 'Lucknow',
    Indore = 'Indore',
    Kanpur = 'Kanpur',
    Pune = 'Pune',
    Faridabad = 'Faridabad',
    Ghaziabad = 'Ghaziabad',
    Noida = 'Noida',
    Chandigarh = 'Chandigarh',
    Ludhiana = 'Ludhiana',
    Referral = 'Referral',
}

export interface DoctorProfile extends DoctorProfileLite {
    tags: Tag[];
    phone: string,
    gender: UserGender,
    dob: string,
    district: string,
    registration_no: string,
    experience: number,
    hospital: string,
    consultation_fee: number,
    time_per_patient: number,
    education_description: string,
    education_description_line_2: string,
    speciality: string,
    speciality_name: string,
    photo: string,
    about_me: string,
    profile_links: string,
    google_place_id: string,
    slug: string,
    app_access_state: number,
    preferred_time: string,
    clinic_address: string,
    organisation: string,
    notes_for_careteam: { [key: string]: string },
    preferred_careplan_tier: ProgramTier,
    inside_sales_call_sensitivity: "YES" | "NO",
    cl_bd_area: CLBDArea,
    assistant_payout_enabled: boolean,
    is_careplan_white_labeled: boolean
}

export enum TeamUsecase {
    careteam = 'careteam',
    bd = 'bd'
}

export interface Team extends BaseModel {
    name: string,
    usecase: TeamUsecase,
    owner: string
}

export interface TeamMember {
    user: DoctorProfileLite,
    team: Team
}
